import React from "react";
import { motion } from "framer-motion";
import { Compass, Flame, Hammer, Trophy, ArrowRight, Users, Sparkles } from "lucide-react";

/**
 * Landing-page "How It Works" section — a scroll-triggered animated
 * explainer that replaces a traditional recorded video. Four steps
 * with iconography, dwell animations, and a concluding CTA.
 */
const STEPS = [
    {
        n: "01",
        icon: Compass,
        title: "Land on the Atlas.",
        body: "Every dot is a discipline — sciences, arts, trades, humanities, business. Zoom, pinch, explore what's actually out there.",
        color: "#6F00FF",
        soft: "#EEE4FF",
    },
    {
        n: "02",
        icon: Flame,
        title: "Light a beacon.",
        body: "Pick a discipline that grips you. Enter as an Observer to learn, or a Contributor to build. Your beacon is public — others see you're here.",
        color: "#FFC107",
        soft: "#FFF3CC",
    },
    {
        n: "03",
        icon: Hammer,
        title: "Take microtasks, join workshops.",
        body: "Real projects run inside every discipline. Complete small tasks for XP. Team up in workshops. Post bounties or apply to roles.",
        color: "#3EB489",
        soft: "#DBF3E9",
    },
    {
        n: "04",
        icon: Trophy,
        title: "Rise. Merge. Master.",
        body: "Earn XP, level up, become a Master of your branch. Bring friends — every invite is a free month. This is your public resume of impact.",
        color: "#FF2052",
        soft: "#FFD4DE",
    },
];

export default function HowItWorks() {
    return (
        <section className="relative w-full py-24 sm:py-32 px-6 z-10" data-testid="how-it-works">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="text-center max-w-3xl mx-auto"
                >
                    <p className="chip mx-auto" style={{ background: "rgba(255,255,255,0.55)", color: "#0A2540" }}>
                        <Sparkles className="w-3 h-3" /> How Atlas Works
                    </p>
                    <h2
                        className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter text-white leading-[0.98] mt-4"
                        style={{ textShadow: "0 2px 24px rgba(10,37,64,0.35)" }}
                        data-testid="how-heading"
                    >
                        From curiosity to contribution
                        <br />
                        <span className="text-arts">in four moves.</span>
                    </h2>
                    <p className="text-white/85 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
                        Atlas isn't another social feed. It's a live map where every discipline has real projects, real people, and real work to do. Here's the flow.
                    </p>
                </motion.div>

                {/* Steps */}
                <div className="mt-16 space-y-6" data-testid="how-steps">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        const flip = i % 2 === 1;
                        return (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 40, scale: 0.98 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                                className="crystal rounded-3xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 items-center"
                                style={{ background: `linear-gradient(${flip ? "270deg" : "90deg"}, ${s.soft}, #ffffff)` }}
                                data-testid={`how-step-${i + 1}`}
                            >
                                <motion.div
                                    className="flex items-center gap-4"
                                    animate={{ y: [0, -3, 0] }}
                                    transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <div
                                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl flex items-center justify-center flex-shrink-0"
                                        style={{ background: s.color, boxShadow: `0 12px 40px ${s.color}66` }}
                                    >
                                        <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                    </div>
                                    <div className="md:hidden">
                                        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: s.color }}>Step {s.n}</div>
                                        <h3 className="font-display font-black text-2xl tracking-tighter text-slate-900 leading-tight">{s.title}</h3>
                                    </div>
                                </motion.div>
                                <div>
                                    <div className="hidden md:block text-xs uppercase tracking-widest font-bold" style={{ color: s.color }}>Step {s.n}</div>
                                    <h3 className="hidden md:block font-display font-black text-3xl tracking-tighter text-slate-900 leading-tight mt-1">{s.title}</h3>
                                    <p className="text-slate-700 text-base sm:text-lg mt-2 leading-relaxed">{s.body}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Trust stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="mt-14 grid grid-cols-3 gap-3 max-w-3xl mx-auto"
                    data-testid="how-stats"
                >
                    {[
                        { n: "150+", l: "Disciplines mapped" },
                        { n: "5", l: "Branches of knowledge" },
                        { n: "$2/mo", l: "Full access · cancel anytime" },
                    ].map((s) => (
                        <div key={s.l} className="crystal rounded-2xl p-4 text-center">
                            <div className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-slate-900 leading-none">{s.n}</div>
                            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mt-1">{s.l}</div>
                        </div>
                    ))}
                </motion.div>

                {/* Closing CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
                >
                    <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="arcade-btn" data-variant="arts" data-testid="how-cta-signup">
                        Sign up free <ArrowRight className="w-4 h-4" />
                    </a>
                    <div className="text-white/80 text-sm flex items-center gap-2">
                        <Users className="w-4 h-4" /> Bring a friend, both get a free month.
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
